import { compact, isArray, isNil, last } from 'lodash';

const errorList = {
  internetConnectionError: 'Check internet connection',
  unknownError: 'Something went wrong. Try to reload the page',
};

const getKnownErrors = (serverErrors = [], knownErrorsMap = {}) => {
  // @todo add error codes or receive error message from the server @feshchenko
  // @todo bring error message handling to a single standard @apavlosiuk
  return compact(
    serverErrors.map(
      error =>
        knownErrorsMap[(error.meta && error.meta.type) || error.description],
    ),
  );
};

export const getResponseErrorMessages = (
  response,
  { knownErrorsMap = {}, includeErrorNames = true } = {},
) => {
  if (!response) {
    return [errorList.internetConnectionError];
  }

  const knownErrors = getKnownErrors(response.data.errors, knownErrorsMap);
  if (knownErrors.length > 0) {
    return knownErrors;
  }

  if (response.data.errors) {
    // @todo clarify with backend if it's correct @feshchenko
    return response.data.errors.map(error => {
      const { description = '', name = '' } = error;
      const errorName = includeErrorNames && name ? `${name} ` : '';
      const errorDescription = description
        ? `${includeErrorNames ? `(${description})` : `${description}`}`
        : '';

      return `${errorName}${errorDescription}`;
    });
  }

  // @todo clarify with backend possible values @feshchenko
  if (response.data.message) {
    return [response.data.message];
  }

  return [errorList.unknownError];
};

export const getErrorPointer = source => {
  return isNil(source) ? null : last(source.pointer.split('/'));
};

export const getJsonApiSubmitError = (
  response,
  submissionErrorMap,
  expectedSingleFieldName = 'value',
) => {
  if (!response || !response.errors) {
    return;
  }

  const errorList = response.errors.reduce((result, { meta, source }) => {
    if (!meta) {
      return result;
    }

    const errorType = meta.type;
    const fieldName = getErrorPointer(source) || expectedSingleFieldName;

    const knownError = submissionErrorMap[errorType];
    if (!knownError) {
      return result;
    }

    const errorObject = {
      [fieldName]: knownError,
    };
    return {
      ...result,
      ...errorObject,
    };
  }, null);

  return {
    submissionError: errorList,
  };
};

export const getSubmissionError = (response, submissionErrorMap) => {
  if (
    !response ||
    !response.data ||
    !response.data.errors ||
    !submissionErrorMap
  ) {
    return;
  }

  return response.data.errors.reduce(
    (result, { name, detail, description, source }) => {
      const errorName = getErrorPointer(source) || name;
      const knownError = submissionErrorMap[errorName];
      if (!knownError) {
        return result;
      }
      // @todo get rid of 'description', cause this is legacy @lukavyi
      const errorMessageName = detail || description;
      const knownErrorMessage = knownError.errors[errorMessageName];
      if (!knownErrorMessage) {
        return result;
      }

      const formFieldNameArray = isArray(knownError.formFieldName)
        ? knownError.formFieldName
        : [knownError.formFieldName];

      const errorsObject = formFieldNameArray.reduce(
        (prev, curr) => ({
          ...prev,
          [curr]: knownErrorMessage,
        }),
        {},
      );
      return {
        ...result,
        ...errorsObject,
      };
    },
    null,
  );
};

export const generateApiError = (config = {}) => (rejection = {}) => {
  const {
    submissionErrorMap,
    knownErrorsMap,
    isClientError,
    isCancelled,
  } = config;
  const { response } = rejection;

  return Promise.reject({
    submissionError: getSubmissionError(response, submissionErrorMap),
    errorMessages: getResponseErrorMessages(response, { knownErrorsMap }),
    rejection,
    isClientError,
    isCancelled,
  });
};
